import {IMessage, IRuleset} from "../../../index";
import {collectUniqueRulesetsFromRoots} from "../../ruleset/copy-wizard/copyRulesetGraph";

export type TCopyPreviewKind = "messageflow" | "ruleset";

export interface ICopyPreviewRow {
    kind: TCopyPreviewKind;
    message?: IMessage;
    ruleset?: IRuleset;
}

/**
 * Unique rulesets referenced (directly or transitively via sub-ruleset rules) by the selected message flows,
 * then builds a **display** list: each root flow followed by rulesets from that flow’s closure that have not
 * yet been shown (deduped by ruleset id across the whole wizard).
 */
export interface IMessageFlowRulesetPlanOptions {
    /** When false, the message flow’s ruleset and its sub-ruleset closure are omitted from the copy plan. Default true. */
    includeMessageMainRuleset?: boolean;
}

export function collectRulesetClosureForMessageFlows(
    rootFlows: IMessage[],
    rulesetsById: Map<number, IRuleset>,
    options?: IMessageFlowRulesetPlanOptions,
): IRuleset[] {
    const includeMain = options?.includeMessageMainRuleset !== false;
    if (!includeMain) {
        return [];
    }
    const directRoots: IRuleset[] = [];
    for (const flow of rootFlows) {
        if (flow.rulesetid) {
            const rs = rulesetsById.get(flow.rulesetid);
            if (rs) {
                directRoots.push(rs);
            }
        }
    }
    return collectUniqueRulesetsFromRoots(directRoots, rulesetsById);
}

export function buildOrderedPreviewRows(
    rootFlows: IMessage[],
    uniqueRulesets: IRuleset[],
    includeMessageMainRuleset = true,
): ICopyPreviewRow[] {
    const rulesetsById = new Map(uniqueRulesets.map((r) => [r.id, r]));
    const shownRuleIds = new Set<number>();
    const rows: ICopyPreviewRow[] = [];
    for (const flow of rootFlows) {
        rows.push({kind: "messageflow", message: flow});
        if (!includeMessageMainRuleset || !flow.rulesetid) {
            continue;
        }
        const seed = rulesetsById.get(flow.rulesetid);
        if (!seed) {
            continue;
        }
        const localClosure = collectUniqueRulesetsFromRoots([seed], rulesetsById);
        for (const rs of localClosure) {
            if (!shownRuleIds.has(rs.id)) {
                shownRuleIds.add(rs.id);
                rows.push({kind: "ruleset", ruleset: rs});
            }
        }
    }
    return rows;
}
